import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProductService } from '../pages/service/product.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { OrderDraftService } from '../services/order-draft.service';
import { ItemDetailsService } from '../services/item-details.service';
import { UserService } from '../pages/service/user.service';
import { ItemOrderHandlerService } from '../item-details/item-order-handler.service';
import { RoleAccessService } from '../auth/role-access.service';
import { RulesService } from '../services/rules.service';
import { InvoiceRuleService } from '../services/invoice-rule.service';
import { LoaderService } from '../services/loader.service';
import { ArStatementService } from '../services/ArStatementService';
import { ArStatementFormComponent } from '../shared/ar-statement-form/ar-statement-form.component';
import { ItemDetailsComponent } from '../item-details/item-details.component';
import { ToolbarModule } from 'primeng/toolbar';
import { V2SidebarComponent } from './components/v2-sidebar/v2-sidebar.component';
import { V2AddRowComponent } from './components/v2-add-row/v2-add-row.component';
import { V2LinesGridComponent } from './components/v2-lines-grid/v2-lines-grid.component';
import {
    RecommendationSelectEvent,
    RecommendationStockState,
    V2RecommendationsComponent
} from './components/v2-recommendations/v2-recommendations.component';
import { V2OrderLine } from './models/item-details-v2.models';
import { HeaderInfoComponent } from '../item-details/header-info/header-info.component';
import { CreditLimitDialogContentComponent } from '../item-details/credit-limit-dialog-content/credit-limit-dialog-content.component';
import { MarginDialogContentComponent } from '../item-details/margin-dialog-content/margin-dialog-content.component';
import { MarginApprovalDialogContentComponent } from '../item-details/margin-approval-dialog-content/margin-approval-dialog-content.component';
import { ItemBrowsePopupComponent } from '../shared/item-browse-popup/item-browse-popup.component';
import { OldSalesPricePopupComponent } from '../shared/old-sales-price-popup/old-sales-price-popup.component';
import { ItemStockLotsPopupComponent } from './components/item-stock-lots-popup/item-stock-lots-popup.component';
import { LoggerService } from '../services/logger.service';
import { MarginValidationService } from '../services/margin-validation.service';
import { ExcelOrderImportService } from './services/excel-order-import.service';
import {
    isMarginApprovedOnOrder,
    shouldSkipMarginProcessingGate,
    showLineMarginColumns
} from '../item-details/margin-approval.utils';
import { ExcelSkippedRow } from './services/excel-order-import.utils';
import { enrichLinesWithRecommendationOverlap, RECO_WAS_FALSE, RECO_WAS_NO_RECO, RECO_WAS_TRUE } from './utils/recommendation-overlap.util';
import { apiTraceFlow } from '../shared/api-trace';
import {
        calculateRowAmounts,
        canEditPriceInM3,
        canEditPricesFromOrderHeader,
        clampDiscountPercent,
        effectiveShippedQty,
        isCreditRestrictionActive,
        isPriceDirtyVsBaseline,
        mapShipQtyFields,
        applyMarginFromItemDetailsToLine,
        resolveLineNoDecPl,
        round2,
        seedPriceBaselines
} from '../item-details/item-utils';
import { applyRunningStockCheck, isStockCheckExcludedItem } from '../item-details/utils/order-line-stock.util';
import {
    applyCopyModeToLine,
    applyVatPolicyAndRecalcAmounts,
    rebindHeaderToTargetCustomer,
    resolveNumericCustomerId,
    shouldFetchVatForCustomer,
    TargetCustomerSnapshot
} from './services/copy-order-different-customer.util';
import { environment } from '../../environments/environment';
import { buildSummaryFromLines } from './adapters/item-details-v2.adapter';
import { OrderRecommendation, OrderRecommendationsResponse } from '../services/item-details.models';
import { EMPTY, Subject, forkJoin, from, of, timer } from 'rxjs';
import { catchError, concatMap, debounce, distinctUntilChanged, finalize, map, reduce, switchMap, takeUntil, toArray } from 'rxjs/operators';

@Component({
    selector: 'app-item-details-v2',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, AutoCompleteModule, ButtonModule, DialogModule, ToastModule, TooltipModule, ToolbarModule, ArStatementFormComponent, V2SidebarComponent, V2AddRowComponent, V2LinesGridComponent, V2RecommendationsComponent, HeaderInfoComponent, CreditLimitDialogContentComponent, MarginDialogContentComponent, MarginApprovalDialogContentComponent, ItemBrowsePopupComponent, ItemStockLotsPopupComponent, OldSalesPricePopupComponent, InputTextModule, TextareaModule],
    providers: [ProductService, MessageService],
    templateUrl: './item-details-v2.component.html',
    styleUrls: ['./item-details-v2.component.scss']
})
export class ItemDetailsV2Component extends ItemDetailsComponent {
    readonly currencySymbol = '€';
    private readonly v2MessageService: MessageService;
    private readonly v2ItemDetailsService: ItemDetailsService;
    private readonly v2ItemOrderHandler: ItemOrderHandlerService;
    private readonly v2Route: ActivatedRoute;
    private readonly v2Router: Router;
    private readonly v2LoaderService: LoaderService;
    private readonly v2Http: HttpClient;
    private readonly v2InMemoryStorage: InMemoryStorageService;
    private readonly itemSearchQuery$ = new Subject<string>();
    private readonly v2SearchDestroy$ = new Subject<void>();
    private v2SearchPipelineSubscribed = false;
    private pendingFocusItemSearch = false;
    private v2ItemSearchSeq = 0;

    /** Synced from app-v2-lines-grid bulk-edit (toolbar Edit/Done label). */
    linesBulkEdit = false;

    /** Synced-order mode: unlock Rate/Disc only (like Edit, but price-only). */
    priceEditMode = false;

    /** Prevents double-submit of Update Prices. */
    updatePricesInFlight = false;

    /** Highlights the selected line when viewing item info via grid selection. */
    selectedLineKey = '';

    @ViewChild(V2AddRowComponent) private v2AddRow?: V2AddRowComponent;
    @ViewChild(HeaderInfoComponent) private headerInfo?: HeaderInfoComponent;
    @ViewChild(V2SidebarComponent) private v2Sidebar?: V2SidebarComponent;
    @ViewChild('linesGrid') linesGrid?: V2LinesGridComponent;
    @ViewChild('excelFileInput') private excelFileInput?: ElementRef<HTMLInputElement>;

    showStockLotsPopup = false;
    stockLotsItemNo = '';

    excelSkippedRows: ExcelSkippedRow[] = [];
    showExcelSkippedDialog = false;
    showExcelImportDialog = false;
    excelImportInProgress = false;

    /** After Save as Draft (100/300): offer Sync when already Sync-ready. V1 does not use this. */
    syncAfterDraftDialogVisible = false;
    private syncAfterDraftOrderNumber = '';

    /**
     * One-shot guard for the section auto-state. Once we've collapsed customer/header
     * and expanded item information (either because the user added their first article
     * or because the order loaded with existing lines), we leave the panels alone so
     * the user can toggle them freely.
     */
    private hasAutoAppliedLinesPresentState = false;

    orderRecommendations: OrderRecommendation[] = [];
    recommendationsLoading = false;
    /** Live M3 stock for the 10 visible suggestion cards only. */
    recommendationStockByItemId: Record<string, RecommendationStockState> = {};
    private recommendationsLoadSeq = 0;
    private readonly recommendationStockCancel$ = new Subject<void>();
    private readonly recommendationVisibleLimit = 10;
    /** Last recommendations API run id (stored on lines added from that panel). */
    private recommendationsRunId: string | null = null;
    /** Full last fetch (before local grid exclude) — used for MANUAL overlap + NO_RECO. */
    private lastFetchedRecommendations: OrderRecommendation[] = [];
    /** HAS = fetch returned items; NONE = fetch completed empty; UNKNOWN = loading / panel off. */
    private recommendationsAvailability: 'HAS' | 'NONE' | 'UNKNOWN' = 'UNKNOWN';
    /** Context key of the last successful GetOrderRecommendations call (dedupe). */
    private lastRecommendationsContextKey: string | null = null;
    /**
     * True while copy-order is in progress. Suppresses the early getPriceInfo() reco load
     * so we only fetch once after lines are ready (afterCopiedOrderLinesLoaded / finishDifferentCustomerCopy).
     */
    private copyInProgressForRecommendations = false;
    private copyRecommendationsWatchdog: ReturnType<typeof setTimeout> | null = null;
    /** Set when user picks a card; consumed on add so rank survives panel removal. */
    private pendingRecommendationSelect: { itemId: string; rank: number | null; runId: string | null } | null = null;
    private readonly v2Storage: InMemoryStorageService;

    constructor(
        inMemoryStorageService: InMemoryStorageService,
        messageService: MessageService,
        route: ActivatedRoute,
        router: Router,
        productService: ProductService,
        orderDraftService: OrderDraftService,
        inMemoryStorage: InMemoryStorageService,
        http: HttpClient,
        itemDetailsService: ItemDetailsService,
        marginValidationService: MarginValidationService,
        userService: UserService,
        itemOrderHandler: ItemOrderHandlerService,
        roleAccessService: RoleAccessService,
        rulesService: RulesService,
        invoiceRuleService: InvoiceRuleService,
        loaderService: LoaderService,
        arStatementService: ArStatementService,
        private readonly logger: LoggerService,
        private readonly excelImportService: ExcelOrderImportService
    ) {
        super(
            inMemoryStorageService,
            messageService,
            route,
            router,
            productService,
            orderDraftService,
            inMemoryStorage,
            http,
            itemDetailsService,
            marginValidationService,
            userService,
            itemOrderHandler,
            roleAccessService,
            rulesService,
            invoiceRuleService,
            loaderService,
            arStatementService
        );
        this.v2Storage = inMemoryStorageService;
        this.v2MessageService = messageService;
        this.v2ItemDetailsService = itemDetailsService;
        this.v2ItemOrderHandler = itemOrderHandler;
        this.v2Route = route;
        this.v2Router = router;
        this.v2LoaderService = loaderService;
        this.v2Http = http;
        this.v2InMemoryStorage = inMemoryStorage;
        this.setupV2ItemSearchPipelines();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.applyWorkingPanelLayoutOnce(true);
    }

    /**
     * V2-only: when nav state includes a different target customer, rebind header and
     * re-resolve VAT/margin/stock while keeping qty/rate/discount (and FACI/WH/OT).
     * Same-customer / missing target falls through to the base implementation.
     */
    protected override loadCopiedOrder(orderNumber: string, copyMode: string = 'ALL'): void {
        // Suppress getPriceInfo → reco load until lines finish (avoids double RECO_LOAD + 20× stock).
        this.beginCopyRecommendationsGate();

        const targetCustomerId = resolveNumericCustomerId(this.navState?.targetCustomerId);
        const snapshot = this.navState?.targetCustomerSnapshot as TargetCustomerSnapshot | undefined;
        if (!targetCustomerId || !snapshot) {
            super.loadCopiedOrder(orderNumber, copyMode);
            return;
        }

        this.v2LoaderService.show(
            `Copying order for customer ${targetCustomerId}…`,
            'Recalculating VAT, margin, and stock'
        );
        this.loadCopiedOrderForDifferentCustomer(orderNumber, copyMode, targetCustomerId, snapshot);
    }

    private loadCopiedOrderForDifferentCustomer(
        orderNumber: string,
        copyMode: string,
        targetCustomerId: string,
        snapshot: TargetCustomerSnapshot
    ): void {
        const company = this.company;
        const division = this.division;
        const orderType = 'CustomerOrder';
        const today = this.getTodayDateForRequest();
        const draftOrderKey = String(this.orderId || this.v2Route.snapshot.paramMap.get('id') || '');

        this.v2ItemDetailsService.getOrderHeader(orderNumber, company, division, orderType).subscribe({
            next: (response: any) => {
                const sourceCustomerId = resolveNumericCustomerId(
                    response?.customerId || response?.CUNO || response?.customer
                );
                if (sourceCustomerId && sourceCustomerId === targetCustomerId) {
                    this.v2LoaderService.hide();
                    super.loadCopiedOrder(orderNumber, copyMode);
                    return;
                }

                console.log('[CopyDiffCustomer] rebind', {
                    sourceCustomerId,
                    targetCustomerId,
                    facilityId: response?.facilityId || response?.facility,
                    whsId: response?.whsId || response?.warehouseId,
                    orderTypeId: response?.orderTypeId || response?.orderType,
                    copyMode
                });

                const rebound = rebindHeaderToTargetCustomer(response, targetCustomerId, snapshot);
                let address = snapshot.address || '';
                let phoneNumber = snapshot.phoneNumber || '';
                let customerAddress = snapshot.customerAddress || '';
                if (!address && rebound.customerAddressDTOResponse) {
                    const addr = rebound.customerAddressDTOResponse;
                    address = [addr.addressLine1, addr.addressLine2, addr.addressLine3, addr.addressLine4]
                        .filter((line: string) => !!line && line.trim() !== '.')
                        .join(', ');
                    phoneNumber = addr.phoneNumber || phoneNumber;
                    customerAddress = addr.combinedAddress || customerAddress;
                }

                this.product = { ...rebound };
                this.customerName = rebound.customerName || rebound.name || 'No customer selected';
                this.item = {
                    ...rebound,
                    id: draftOrderKey || rebound.id,
                    userName: response.userDto?.userName,
                    address,
                    phoneNumber,
                    combinedAddress: customerAddress,
                    customerId: targetCustomerId,
                    customerName: this.customerName,
                    orderNumber: 'New Order',
                    orderId: undefined,
                    addressId: snapshot.addressId || '',
                    addressCountryCode: snapshot.addressCountryCode || '',
                    countryCode: snapshot.addressCountryCode || '',
                    isCustom: snapshot.isCustom === 1 ? 1 : 0,
                    customAddress: snapshot.isCustom === 1 ? snapshot.customAddress || '' : '',
                    facilityId: response.facilityId || response.facility || '',
                    warehouseId: response.whsId || response.warehouseId || '',
                    whsId: response.whsId || response.warehouseId || '',
                    orderTypeId: response.orderTypeId || response.orderType || ''
                };
                this.product.name = this.customerName;
                this.product.customerId = targetCustomerId;
                this.product.orderNumber = 'New Order';
                this.product.facilityId = this.item.facilityId;
                this.product.whsId = this.item.whsId;
                this.product.warehouseId = this.item.warehouseId;
                this.product.orderTypeId = this.item.orderTypeId;
                this.product.addressId = this.item.addressId;
                this.product.address = address;
                this.product.addressCountryCode = this.item.addressCountryCode;
                this.product.isCustom = this.item.isCustom;
                this.product.customAddress = this.item.customAddress;

                this.clearCopiedOrderCreditAndSyncFields();
                this.item.requestedDate = today;
                this.product.requestedDate = today;
                this.item.status = 10;
                this.product.status = 10;
                this.item.orderMovingStatus = '';
                this.product.orderMovingStatus = '';
                this.warehouse = this.item.whsId || '';

                this.selectedCustomerNumber = targetCustomerId;
                this.selectedCustomerName = this.customerName;

                this.v2ItemDetailsService.getOrderLines(orderNumber, company, division).subscribe({
                    next: (linesResponse: any) => {
                        const content = linesResponse?.content || [];
                        let mappedLines: any[] = [];
                        if (Array.isArray(content)) {
                            mappedLines = content.map((item: any) => {
                                const amount = item.grossAmount ?? item.gross ?? 0;
                                const sourceCost =
                                    item?.costPrice != null && item.costPrice !== ''
                                        ? Number(item.costPrice)
                                        : undefined;
                                const sourceMcm =
                                    item?.itemMcm != null && item.itemMcm !== ''
                                        ? Number(item.itemMcm)
                                        : undefined;
                                const baseLine = {
                                    orderNumber: draftOrderKey,
                                    lineNumber: String(item.lineNo),
                                    itemName: item.itemName || '',
                                    itemNumber: item.itemNo || '',
                                    quantity: item.qty ?? 0,
                                    rate: item.rate ?? 0,
                                    amount,
                                    discountAmount: item.discountVal ?? 0,
                                    discount: item.discount ?? 0,
                                    amountAfterDiscount: item.netAmount ?? 0,
                                    vat: item.vat ?? 0,
                                    vatAmount: item.vatAmount ?? 0,
                                    amountAfterVat: item.lineAmount ?? 0,
                                    pcs: item.pcs,
                                    size: item.size,
                                    itemGroup: item.itemGroup,
                                    netWeight: item.netWeight,
                                    grossWeight: item.grossWeight,
                                    vatCode: item.vatCode,
                                    status: 10,
                                    noDecPl: resolveLineNoDecPl(item, 2),
                                    // Seed from source order; ItemDetails refresh below overwrites when non-zero.
                                    cost: Number.isFinite(sourceCost as number) ? sourceCost : undefined,
                                    minimumMargin: Number.isFinite(sourceMcm as number) ? sourceMcm : undefined,
                                    lineCost:
                                        Number.isFinite(sourceCost as number)
                                            ? round2((sourceCost as number) * Number(item.qty ?? 0))
                                            : undefined
                                };
                                return applyCopyModeToLine(baseLine, copyMode);
                            });
                        }

                        this.resolveWarehouseCountryThenEnrich(
                            mappedLines,
                            copyMode || 'ALL',
                            targetCustomerId,
                            this.warehouse
                        );
                    },
                    error: () => {
                        this.v2LoaderService.hide();
                        this.loading = false;
                        this.v2MessageService.add({
                            severity: 'error',
                            summary: 'Failed to load lines',
                            detail: 'Could not load order lines. Please try again.',
                            life: 3000
                        });
                    }
                });
            },
            error: () => {
                this.v2LoaderService.hide();
                this.loading = false;
                this.v2MessageService.add({
                    severity: 'error',
                    summary: 'Order not found',
                    detail: 'Could not find the order. Check the order number and try again.',
                    life: 3000
                });
                setTimeout(() => this.v2Router.navigate(['/Orders1']), 300);
            }
        });
    }

    private resolveWarehouseCountryThenEnrich(
        mappedLines: any[],
        copyMode: string,
        targetCustomerId: string,
        whlo: string
    ): void {
        const companyId = String(this.v2InMemoryStorage.get('company') || this.company || '');
        const applyAndEnrich = () => {
            this.calculateVatPercentage();
            const shouldFetchVat = shouldFetchVatForCustomer({
                txap: this.product?.txap ?? this.item?.txap,
                customerCountryCode:
                    this.product?.addressCountryCode ||
                    this.item?.addressCountryCode ||
                    this.product?.countryCode ||
                    this.item?.countryCode,
                warehouseCountryCode: this.selectedWarehouseCountryCode
            });
            this.product.shouldFetchVatPercentage = shouldFetchVat;
            this.item.shouldFetchVatPercentage = shouldFetchVat;

            console.log('[CopyDiffCustomer] vatPolicy', {
                targetCustomerId,
                txap: this.product?.txap,
                shouldFetchVat,
                warehouseCountry: this.selectedWarehouseCountryCode,
                customerCountry: this.product?.addressCountryCode
            });

            this.getPriceInfo();
            this.setPriceListsByPltb(this.product?.pltb || this.item?.pltb);

            if (mappedLines.length === 0) {
                this.itemList = [];
                this.setItemNamesForAutocomplete();
                this.resetForm();
                this.setDraftOrders();
                localStorage.setItem('orderLines', JSON.stringify([]));
                this.v2LoaderService.hide();
                this.loading = false;
                this.v2MessageService.add({
                    severity: 'success',
                    summary: 'Order copied',
                    detail: 'Order header loaded for the new customer. No lines to copy.',
                    life: 3000
                });
                this.applyWorkingPanelLayoutOnce(false);
                return;
            }

            this.enrichCopiedLinesForDifferentCustomer(mappedLines, copyMode, targetCustomerId, whlo, shouldFetchVat);
        };

        if (!companyId) {
            applyAndEnrich();
            return;
        }

        this.v2Http
            .get<any[]>(`${environment.apiUrl}facilities/LstWarehouses?company=${companyId}`, this.getAuthOptions())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.warehousesForVat = (data || []).map((w) => ({
                        value: w.whlo,
                        label: `${w.whlo} - ${w.whnm}`,
                        cscd: w.cscd
                    }));
                    const orderWarehouse = this.warehousesForVat.find((w) => String(w.value) === String(whlo));
                    this.selectedWarehouseCountryCode = orderWarehouse?.cscd || '';
                    applyAndEnrich();
                },
                error: () => {
                    applyAndEnrich();
                }
            });
    }

    private enrichCopiedLinesForDifferentCustomer(
        mappedLines: any[],
        copyMode: string,
        targetCustomerId: string,
        whlo: string,
        shouldFetchVat: boolean
    ): void {
        const company = this.company;
        const division = this.division;
        const mode = copyMode || 'ALL';
        const runStock = mode === 'ALL';
        apiTraceFlow('COPY_ORDER', {
            lines: mappedLines.length,
            company,
            division,
            customerId: targetCustomerId
        });
        const CHUNK_SIZE = 10;
        const chunks: any[][] = [];
        for (let i = 0; i < mappedLines.length; i += CHUNK_SIZE) {
            chunks.push(mappedLines.slice(i, i + CHUNK_SIZE));
        }

        const stockLedger: Record<string, number> = {};
        let failedItemDetails = 0;

        const applyItemDetailsResult = (line: any, res: any | null) => {
            if (!res) {
                failedItemDetails += 1;
                if (!shouldFetchVat) {
                    Object.assign(line, applyVatPolicyAndRecalcAmounts(line, { shouldFetchVat: false }));
                } else {
                    Object.assign(line, calculateRowAmounts(line));
                }
                return;
            }

            applyMarginFromItemDetailsToLine(
                line,
                res,
                division,
                this.product?.defaultPriceList || this.item?.defaultPriceList || '',
                effectiveShippedQty(line)
            );

            const itemDetails = res?.ItemDetails;
            if (itemDetails) {
                const stcd = itemDetails.STCD ?? itemDetails.AR;
                if (stcd !== undefined && stcd !== null && stcd !== '') {
                    line.ar = Number(stcd);
                }
                if (itemDetails.vatCode !== undefined && itemDetails.vatCode !== null && itemDetails.vatCode !== '') {
                    line.vatCode = itemDetails.vatCode;
                }
                if (itemDetails.pcs !== undefined) {
                    line.pcs = itemDetails.pcs;
                }
                if (itemDetails.size !== undefined) {
                    line.size = itemDetails.size;
                }
            }

            const vatPercent =
                res?.vatFields?.VTP1 !== undefined && res?.vatFields?.VTP1 !== null
                    ? Number(res.vatFields.VTP1)
                    : null;
            Object.assign(
                line,
                applyVatPolicyAndRecalcAmounts(line, {
                    shouldFetchVat,
                    vatPercent,
                    vatCode: line.vatCode
                })
            );

            if (runStock && Number(line.quantity ?? 0) > 0) {
                const itemStockCount = itemDetails?.itemStockCount;
                const count =
                    itemStockCount !== undefined && itemStockCount !== null ? Number(itemStockCount) : null;
                const apiStock = count !== null && !isNaN(count) ? count : null;
                applyRunningStockCheck(
                    line,
                    apiStock,
                    stockLedger,
                    this.ruleActiveForDivision,
                    isStockCheckExcludedItem(line.itemNumber)
                );
            }
        };

        from(chunks)
            .pipe(
                concatMap((chunkLines, chunkIndex) => {
                    const baseIdx = chunkIndex * CHUNK_SIZE;
                    const calls = chunkLines.map((line: any) =>
                        this.v2ItemDetailsService
                            .getItemDetails(
                                company,
                                division,
                                {
                                    itno: line.itemNumber,
                                    cuno: targetCustomerId,
                                    whlo
                                },
                                'COPY_ORDER'
                            )
                            .pipe(catchError(() => of(null)))
                    );
                    return forkJoin(calls).pipe(
                        map((stockResults: (any | null)[]) => {
                            stockResults.forEach((res, idx) => {
                                const line = mappedLines[baseIdx + idx];
                                if (line) {
                                    applyItemDetailsResult(line, res);
                                }
                            });
                            return chunkIndex;
                        })
                    );
                }),
                toArray()
            )
            .subscribe({
                next: () => {
                    this.finishDifferentCustomerCopy(mappedLines, failedItemDetails);
                },
                error: () => {
                    this.finishDifferentCustomerCopy(mappedLines, failedItemDetails + 1);
                }
            });
    }

    private finishDifferentCustomerCopy(mappedLines: any[], failedItemDetails: number): void {
        this.itemList = mappedLines;
        this.refreshMarginFlagsOnItemList();
        this.setItemNamesForAutocomplete();
        this.resetForm();
        this.setDraftOrders();
        localStorage.setItem('orderLines', JSON.stringify(this.itemList));
        this.v2LoaderService.hide();
        this.loading = false;

        const hasStockErrors = mappedLines.some((l: any) => l.copyOrderStockError);
        let detail = hasStockErrors
            ? 'Order copied for the new customer. Fix any lines with stock errors before saving.'
            : 'Order copied for the new customer. Rates/discounts kept; VAT, margin, and stock recalculated.';
        if (failedItemDetails > 0) {
            detail += ` ${failedItemDetails} line(s) could not refresh item details — verify VAT/margin.`;
        }

        this.v2MessageService.add({
            severity: hasStockErrors || failedItemDetails > 0 ? 'warn' : 'success',
            summary: 'Order copied',
            detail,
            life: 5000
        });
        this.applyWorkingPanelLayoutOnce(false);
        console.log('[CopyDiffCustomer] complete', {
            targetCustomerId: this.item?.customerId,
            lineCount: mappedLines.length,
            failedItemDetails,
            hasStockErrors
        });
        // One reco load after lines are ready (getPriceInfo was suppressed during copy).
        this.endCopyRecommendationsGate();
        this.loadOrderRecommendations({ force: true });
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.applyWorkingPanelLayoutOnce(true);
        this.focusV2ItemSearchOnLanding();
    }

    /** Focus item search when the page first loads (add row may render after async init). */
    private focusV2ItemSearchOnLanding(): void {
        if (this.readOnly || this.isReadOnly) {
            return;
        }
        const tryFocus = () => this.v2AddRow?.focusItemSearch();
        [0, 100, 300, 600].forEach((delay) => setTimeout(tryFocus, delay));
    }

    /**
     * Collapse Customer/Header and expand Item Information once per page visit.
     * @param requireLines when true, only runs if itemList already has lines (existing orders).
     */
    private applyWorkingPanelLayoutOnce(requireLines = false): void {
        if (this.hasAutoAppliedLinesPresentState) {
            return;
        }
        if (requireLines && (!this.itemList || this.itemList.length === 0)) {
            return;
        }
        if (!this.headerInfo && !this.v2Sidebar) {
            return;
        }
        this.headerInfo?.collapseAllSections();
        this.v2Sidebar?.setInfoExpanded(true);
        this.hasAutoAppliedLinesPresentState = true;
    }

    override ngOnDestroy(): void {
        this.endCopyRecommendationsGate();
        this.recommendationStockCancel$.next();
        this.recommendationStockCancel$.complete();
        this.v2SearchDestroy$.next();
        this.v2SearchDestroy$.complete();
        super.ngOnDestroy();
    }

    /**
     * V2-only: base `loadOrderFromApi` flow has a bug where `fetchOrderLinesDraft` only
     * mutates a local ctx and never updates the component's `itemList`. After API resolves
     * `this.item.orderNumber` (via `itemSetter` -> `getPriceInfo`), schedule a microtask
     * that runs after the synchronous handler block and restore the user's local edits
     * from `localStorage.orderLines` if they belong to this same order.
     */
    override getPriceInfo(): void {
        super.getPriceInfo();
        queueMicrotask(() => this.applyLocalDraftLinesIfApplicable());
        queueMicrotask(() => {
            if (!this.isDraftOrderNumber(`${this.item?.orderNumber || ''}`.trim())) {
                return;
            }
            // Copy will load reco once after lines; skip the early draft getPriceInfo hit.
            if (this.copyInProgressForRecommendations) {
                return;
            }
            this.loadOrderRecommendations();
        });
    }

    get showRecommendationsPanel(): boolean {
        if (!environment.predictionsRecommendationsEnabled) {
            return false;
        }
        if (this.readOnly || this.isReadOnly) {
            return false;
        }
        return this.hasValidCustomerId;
    }

    private resolveCustomerIdForRecommendations(): string {
        const fromItem = `${this.item?.customerId ?? this.item?.customer ?? ''}`.trim();
        if (fromItem) {
            return fromItem;
        }
        return `${this.product?.customerId ?? this.product?.customer ?? ''}`.trim();
    }

    private isDraftOrderNumber(orderNumber: string): boolean {
        const n = (orderNumber || '').trim();
        return !n || n.toLowerCase() === 'new order';
    }

    private recommendationContextKey(): string {
        const customerId = this.resolveCustomerIdForRecommendations();
        const orderNumber = `${this.item?.orderNumber || ''}`.trim();
        const warehouse = String(
            this.item?.warehouseId ||
                this.item?.whsId ||
                this.item?.warehouse ||
                this.item?.whlo ||
                this.warehouse ||
                ''
        ).trim();
        return [this.company, this.division, customerId, orderNumber, warehouse].join('|');
    }

    private applyLocalRecommendationRemovalForAddedItem(itemId: string | null | undefined): void {
        const normalizedItemId = String(itemId || '').trim();
        if (!normalizedItemId) {
            return;
        }
        const beforeCount = this.orderRecommendations.length;
        this.orderRecommendations = this.orderRecommendations.filter(
            (r) => String(r?.itemId ?? '').trim() !== normalizedItemId
        );
        if (this.orderRecommendations.length === beforeCount) {
            return;
        }
        this.enrichVisibleRecommendationStock(
            this.orderRecommendations.slice(0, this.recommendationVisibleLimit),
            this.recommendationsLoadSeq,
            'fillMissing'
        );
    }

    /**
     * @param opts.force when true, always hit GetOrderRecommendations (copy/excel/delete/open order).
     * Without force, skips API if same customer/order/warehouse context was already loaded.
     */
    loadOrderRecommendations(opts?: { force?: boolean }): void {
        if (!this.showRecommendationsPanel) {
            this.cancelRecommendationStockEnrichment();
            this.orderRecommendations = [];
            this.lastFetchedRecommendations = [];
            this.recommendationStockByItemId = {};
            this.recommendationsRunId = null;
            this.recommendationsAvailability = 'UNKNOWN';
            this.lastRecommendationsContextKey = null;
            return;
        }
        if (this.copyInProgressForRecommendations && !opts?.force) {
            apiTraceFlow('RECO_SKIP', { reason: 'copy_pending' });
            return;
        }

        const contextKey = this.recommendationContextKey();
        const exclude = this.gridItemNumbersForRecommendations();
        if (
            !opts?.force &&
            contextKey &&
            contextKey === this.lastRecommendationsContextKey &&
            this.recommendationsAvailability !== 'UNKNOWN'
        ) {
            // Same header context — reuse last fetch; only re-filter / fill missing stock.
            apiTraceFlow('RECO_SKIP', { reason: 'same_context' });
            this.applyRecommendationOverlapToGridLines(this.lastFetchedRecommendations);
            this.orderRecommendations = this.filterRecommendationsLocally(
                this.lastFetchedRecommendations,
                exclude
            );
            this.enrichVisibleRecommendationStock(
                this.orderRecommendations.slice(0, this.recommendationVisibleLimit),
                this.recommendationsLoadSeq,
                'fillMissing'
            );
            return;
        }

        const orderNumber = `${this.item?.orderNumber || ''}`.trim();
        const customerId = this.resolveCustomerIdForRecommendations();
        const seq = ++this.recommendationsLoadSeq;
        this.cancelRecommendationStockEnrichment();
        this.recommendationStockByItemId = {};
        this.recommendationsLoading = true;
        this.recommendationsAvailability = 'UNKNOWN';
        // Fetch without API exclude so COPY/EXCEL overlap can match items already on the grid;
        // request extra slots so the panel can still show ~20 after local exclude.
        const limit = Math.min(50, Math.max(20, 20 + exclude.length));
        const orderNumberForApi = this.isDraftOrderNumber(orderNumber) ? undefined : orderNumber;
        this.v2ItemDetailsService
            .getOrderRecommendations(
                orderNumberForApi,
                this.company,
                this.division,
                'CustomerOrder',
                limit,
                undefined,
                this.isDraftOrderNumber(orderNumber) ? customerId : undefined,
                'RECO_LOAD'
            )
            .pipe(
                catchError((err) => {
                    this.logger.warn('Order recommendations fetch failed', err as any);
                    return of({ recommendations: [] as OrderRecommendation[], runId: undefined } as OrderRecommendationsResponse);
                }),
                finalize(() => {
                    if (seq === this.recommendationsLoadSeq) {
                        this.recommendationsLoading = false;
                    }
                })
            )
            .subscribe((resp) => {
                if (seq !== this.recommendationsLoadSeq) {
                    return;
                }
                this.recommendationsRunId = resp?.runId ? String(resp.runId) : null;
                const fullList = resp?.recommendations || [];
                this.lastFetchedRecommendations = fullList;
                this.lastRecommendationsContextKey = contextKey;
                this.recommendationsAvailability = fullList.length > 0 ? 'HAS' : 'NONE';
                this.applyRecommendationOverlapToGridLines(fullList);
                this.orderRecommendations = this.filterRecommendationsLocally(fullList, exclude);
                this.enrichVisibleRecommendationStock(
                    this.orderRecommendations.slice(0, this.recommendationVisibleLimit),
                    seq,
                    'replace'
                );
            });
    }

    /**
     * COPY/EXCEL keep add_source; was_recommended/rank/run_id match MANUAL when the item
     * appears in the full recommendation response (must run before local grid exclude).
     * In-memory only — do not write localStorage.orderLines here. Persisting on every
     * recommendations load false-triggered the Orders1 "unsaved draft" dialog on Back
     * even when the user made no edits (needs both orders + orderLines keys).
     */
    private applyRecommendationOverlapToGridLines(recommendations: OrderRecommendation[]): void {
        const lines = this.itemList || [];
        if (!lines.length) {
            return;
        }
        const available =
            this.recommendationsAvailability === 'HAS'
                ? true
                : this.recommendationsAvailability === 'NONE'
                  ? false
                  : null;
        enrichLinesWithRecommendationOverlap(lines, recommendations, this.recommendationsRunId, available);
        this.itemList = [...lines];
    }

    /** After base copy finishes mapping lines, reload recommendations so overlap enrich runs. */
    protected override afterCopiedOrderLinesLoaded(): void {
        this.endCopyRecommendationsGate();
        this.loadOrderRecommendations({ force: true });
    }

    private beginCopyRecommendationsGate(): void {
        this.copyInProgressForRecommendations = true;
        this.lastRecommendationsContextKey = null;
        if (this.copyRecommendationsWatchdog) {
            clearTimeout(this.copyRecommendationsWatchdog);
        }
        // Safety: if copy aborts before afterCopiedOrderLinesLoaded, do not block reco forever.
        this.copyRecommendationsWatchdog = setTimeout(() => {
            if (this.copyInProgressForRecommendations) {
                this.copyInProgressForRecommendations = false;
                this.copyRecommendationsWatchdog = null;
            }
        }, 45000);
    }

    private endCopyRecommendationsGate(): void {
        this.copyInProgressForRecommendations = false;
        if (this.copyRecommendationsWatchdog) {
            clearTimeout(this.copyRecommendationsWatchdog);
            this.copyRecommendationsWatchdog = null;
        }
    }

    private cancelRecommendationStockEnrichment(): void {
        this.recommendationStockCancel$.next();
    }

    /**
     * Live stock for cards currently shown (max 10).
     * - replace: fetch all visible (full recommendations reload)
     * - fillMissing: only fetch items that just scrolled into the visible 10
     *   (e.g. after Select removes one card and #11 takes its place)
     */
    private enrichVisibleRecommendationStock(
        visible: OrderRecommendation[],
        seq: number,
        mode: 'replace' | 'fillMissing' = 'replace'
    ): void {
        this.cancelRecommendationStockEnrichment();
        if (!visible.length) {
            if (mode === 'replace') {
                this.recommendationStockByItemId = {};
            }
            return;
        }

        const cuno = this.resolveCustomerIdForRecommendations();
        const whlo = String(
            this.item?.warehouseId ||
                this.item?.whsId ||
                this.item?.warehouse ||
                this.item?.whlo ||
                this.warehouse ||
                ''
        ).trim();

        const needsFetch = (id: string): boolean => {
            if (!id) {
                return false;
            }
            if (mode === 'replace') {
                return true;
            }
            const existing = this.recommendationStockByItemId[id];
            return !existing || existing.status === 'checking';
        };

        const targets = visible.filter((rec) => needsFetch(String(rec?.itemId ?? '').trim()));

        if (mode === 'replace') {
            const checking: Record<string, RecommendationStockState> = {};
            for (const rec of visible) {
                const id = String(rec?.itemId ?? '').trim();
                if (id) {
                    checking[id] = { status: 'checking' };
                }
            }
            this.recommendationStockByItemId = checking;
        } else {
            const next: Record<string, RecommendationStockState> = {};
            for (const rec of visible) {
                const id = String(rec?.itemId ?? '').trim();
                if (!id) {
                    continue;
                }
                const existing = this.recommendationStockByItemId[id];
                if (needsFetch(id)) {
                    next[id] = { status: 'checking' };
                } else {
                    next[id] = existing;
                }
            }
            this.recommendationStockByItemId = next;
        }

        if (!targets.length) {
            return;
        }

        // Stock-only API needs warehouse; customer id is not required for Lst_CO_ln_Scrp.
        if (!whlo) {
            const failed: Record<string, RecommendationStockState> = {
                ...this.recommendationStockByItemId
            };
            for (const rec of targets) {
                const id = String(rec?.itemId ?? '').trim();
                if (id) {
                    failed[id] = { status: 'error' };
                }
            }
            this.recommendationStockByItemId = failed;
            return;
        }

        const calls = targets.map((rec) => {
            const itno = String(rec?.itemId ?? '').trim();
            return this.v2ItemDetailsService
                .getAvailableStock(this.company, this.division, { itno, whlo }, 'RECO_STOCK')
                .pipe(catchError(() => of(null)));
        });

        forkJoin(calls)
            .pipe(takeUntil(this.recommendationStockCancel$), takeUntil(this.v2SearchDestroy$))
            .subscribe({
                next: (results) => {
                    if (seq !== this.recommendationsLoadSeq) {
                        return;
                    }
                    const next: Record<string, RecommendationStockState> = {
                        ...this.recommendationStockByItemId
                    };
                    results.forEach((res, idx) => {
                        const id = String(targets[idx]?.itemId ?? '').trim();
                        if (!id) {
                            return;
                        }
                        // HTTP failure → cannot verify; successful response with null/empty → treat as 0 stock.
                        if (res == null) {
                            next[id] = { status: 'error' };
                            return;
                        }
                        const raw =
                            res?.itemStockCount ??
                            res?.ItemDetails?.itemStockCount ??
                            res?.ItemDetails?.avlStock;
                        const n = raw != null && raw !== '' ? Number(raw) : 0;
                        next[id] = {
                            status: 'ok',
                            avl: Number.isFinite(n) ? Math.max(0, n) : 0
                        };
                    });
                    this.recommendationStockByItemId = next;
                },
                error: () => {
                    if (seq !== this.recommendationsLoadSeq) {
                        return;
                    }
                    const failed: Record<string, RecommendationStockState> = {
                        ...this.recommendationStockByItemId
                    };
                    for (const rec of targets) {
                        const id = String(rec?.itemId ?? '').trim();
                        if (id) {
                            failed[id] = { status: 'error' };
                        }
                    }
                    this.recommendationStockByItemId = failed;
                }
            });
    }

    onRecommendationSelect(event: RecommendationSelectEvent): void {
        const rec = event?.recommendation;
        const itemId = String(rec?.itemId || '').trim();
        if (!itemId || this.readOnly || this.isReadOnly) {
            return;
        }

        this.clearLineInfoSelection();

        const meta = rec.metadata || {};
        const description = String(
            meta['PRODUCT_DESCRIPTION'] ??
                meta['productDescription'] ??
                meta['ITEM_NAME'] ??
                meta['itemName'] ??
                meta['DESCRIPTION'] ??
                meta['description'] ??
                meta['BRAND'] ??
                meta['brand'] ??
                ''
        ).trim();

        this.itemForm.itemNumber = itemId;
        this.itemForm.itemName = description;
        this.itemForm.FINALRESPNSE = this.buildV2AddRowItemLabel(itemId, description);

        this.itemSelected = true;
        (this as any).suppressItemInputReset = true;
        setTimeout(() => ((this as any).suppressItemInputReset = false));

        const metaPrice = meta['PRICE'] ?? meta['price'];
        if (metaPrice != null && metaPrice !== '') {
            const price = Number(metaPrice);
            if (Number.isFinite(price)) {
                this.itemForm.rate = Number(price.toFixed(2));
            }
        } else if (rec.expectedPriceNext7d != null && Number.isFinite(Number(rec.expectedPriceNext7d))) {
            this.itemForm.rate = Number(Number(rec.expectedPriceNext7d).toFixed(2));
        } else if (rec.expectedPriceNext30d != null && Number.isFinite(Number(rec.expectedPriceNext30d))) {
            this.itemForm.rate = Number(Number(rec.expectedPriceNext30d).toFixed(2));
        }

        const selectedQty = event?.quantity != null ? Number(event.quantity) : NaN;
        if (Number.isFinite(selectedQty) && selectedQty > 0) {
            this.itemForm.quantity = Math.round(selectedQty * 100) / 100;
        } else if (rec.p50QuantityNext7d != null && Number.isFinite(Number(rec.p50QuantityNext7d))) {
            this.itemForm.quantity = Math.round(Number(rec.p50QuantityNext7d) * 100) / 100;
        } else if (rec.p50QuantityNext30d != null && Number.isFinite(Number(rec.p50QuantityNext30d))) {
            this.itemForm.quantity = Math.max(1, Math.round(Number(rec.p50QuantityNext30d)));
        }

        const rank = rec.rank != null && Number.isFinite(Number(rec.rank)) ? Number(rec.rank) : null;
        this.pendingRecommendationSelect = {
            itemId,
            rank,
            runId: this.recommendationsRunId
        };
        this.itemForm.recommendationAddSource = 'RECOMMENDATION_SELECT';
        this.itemForm.recommendationWasRecommended = RECO_WAS_TRUE;
        this.itemForm.recommendationRank = rank;
        this.itemForm.recommendationRunId = this.recommendationsRunId;

        this.onItemNumberChange();
        this.applyWorkingPanelLayoutOnce();

        this.orderRecommendations = this.orderRecommendations.filter((r) => String(r.itemId).trim() !== itemId);
        // #11 (etc.) may slide into the visible 10 — fetch stock only for those new cards.
        this.enrichVisibleRecommendationStock(
            this.orderRecommendations.slice(0, this.recommendationVisibleLimit),
            this.recommendationsLoadSeq,
            'fillMissing'
        );
        setTimeout(() => {
            if (this.itemForm.quantity != null && Number(this.itemForm.quantity) > 0) {
                this.v2AddRow?.focusRate?.();
            } else {
                this.v2AddRow?.focusQuantity();
            }
        }, 150);
    }

    /** Same label shape as V2 item search dropdown (`15165 | COBRA PREMIUM BEER 330 ml`). */
    private buildV2AddRowItemLabel(itemId: string, description: string): string {
        const id = String(itemId || '').trim();
        const desc = String(description || '').trim();
        if (!id) {
            return desc;
        }
        if (!desc) {
            return id;
        }
        const separator = ' | ';
        if (desc.startsWith(id + separator) || desc.startsWith(id + ' ')) {
            return desc;
        }
        return `${id}${separator}${desc}`;
    }

    private gridItemNumbersForRecommendations(): string[] {
        return (this.itemList || [])
            .map((line: any) => String(line?.itemNumber ?? line?.itemNo ?? '').trim())
            .filter(Boolean);
    }

    private filterRecommendationsLocally(list: OrderRecommendation[], exclude: string[]): OrderRecommendation[] {
        const excludeSet = new Set(exclude);
        return list.filter((r) => !excludeSet.has(String(r.itemId).trim()));
    }

    onOrderLineSelectedForInfo(row: V2OrderLine): void {
        if (!row?.itemNumber) {
            return;
        }
        this.clearAddRowForLineInspect();
        this.selectedLineKey = this.buildLineInfoKey(row);
        this.loadItemInfoForOrderLine(row, { inspectOnly: true });
        this.v2Sidebar?.setInfoExpanded(true);
    }

    get sidebarInfoContext(): 'line' | 'addRow' | 'none' {
        if (this.lineInfoView?.itemNumber) {
            return 'line';
        }
        if (this.itemForm?.itemNumber) {
            return 'addRow';
        }
        return 'none';
    }

    /** Public getters for template — protected `lineInfoView` is not visible in the V2 template. */
    get sidebarSelectedItemNumber(): string {
        return String(this.lineInfoView?.itemNumber ?? this.itemForm?.itemNumber ?? '').trim();
    }

    get sidebarSelectedItemName(): string {
        return String(this.lineInfoView?.itemName ?? this.itemForm?.itemName ?? '').trim();
    }

    onViewLots(): void {
        this.stockLotsItemNo = String(this.sidebarSelectedItemNumber || this.itemForm?.itemNumber || '').trim();
        this.showStockLotsPopup = true;
    }

    override openOldSalesPricePopup(): void {
        if (!this.oldSalesPriceCustomer) {
            return;
        }
        this.oldSalesPriceItemNo = String(this.sidebarSelectedItemNumber || this.itemForm?.itemNumber || '').trim();
        this.showOldSalesPricePopup = true;
    }

    get sidebarAvailableQty(): number {
        if (this.lineInfoView != null) {
            return this.lineInfoView.avlStock;
        }
        return Number(this.itemForm?.avlStock) || 0;
    }

    clearLineInfoSelection(): void {
        this.selectedLineKey = '';
        this.lineInfoView = null;
    }

    /** CRT only: clear add-row draft so it does not conflict with grid line inspect. */
    private clearAddRowForLineInspect(): void {
        if (this.isViewOnlyOrderLines) {
            return;
        }

        const lineNumber = this.itemForm?.lineNumber;
        const orderNumber = this.itemForm?.orderNumber;

        this.itemForm.FINALRESPNSE = '';
        this.itemForm.itemNumber = '';
        this.itemForm.itemName = '';
        this.itemForm.quantity = 0;
        this.itemForm.rate = 0;
        this.itemForm.amount = 0;
        this.itemForm.discount = 0;
        this.itemForm.amountAfterDiscount = 0;
        this.itemForm.vatAmount = 0;
        this.itemForm.amountAfterVat = 0;
        this.itemForm.avlStock = 0;
        (this.itemForm as any).apiStock = 0;

        this.itemForm.lineNumber = lineNumber;
        this.itemForm.orderNumber = orderNumber ?? '';

        this.resetV2ItemSearchUiState();
    }

    private buildLineInfoKey(row: { lineNumber?: string | number; itemNumber?: string }): string {
        return `${row.lineNumber ?? ''}-${row.itemNumber ?? ''}`;
    }

    private applyLocalDraftLinesIfApplicable(): void {
        try {
            const orderNumber = `${this.item?.orderNumber || ''}`.trim();
            if (!orderNumber || orderNumber === 'New Order') {
                return;
            }
            const raw = localStorage.getItem('orderLines');
            if (!raw || raw === '[]') {
                return;
            }
            let lines: any[] = [];
            try {
                lines = JSON.parse(raw);
            } catch {
                return;
            }
            if (!Array.isArray(lines) || lines.length === 0) {
                return;
            }
            const draftOrderNum = `${lines[0]?.orderNumber || ''}`.trim();
            if (draftOrderNum !== orderNumber) {
                return;
            }
            this.itemList = lines.map((line: any) => this.mapDraftLineToV2Row(line));
            this.refreshMarginFlagsOnItemList();
            this.logger.debug('V2 draft lines restored from localStorage for orderNumber', orderNumber);
            this.applyWorkingPanelLayoutOnce(true);
        } catch (e) {
            this.logger.warn('V2 draft lines restore failed', e as any);
        }
    }

    /**
     * Local draft lines can be stored in either API-like keys (lineNo/itemNo/qty)
     * or UI-grid keys (lineNumber/itemNumber/quantity). Normalize both shapes.
     */
    private mapDraftLineToV2Row(line: any): any {
        const quantity = Number(line?.qty ?? line?.quantity ?? 0);
        const rate = Number(line?.rate ?? 0);
        const discount = Number(line?.discount ?? 0);
        const vat = Number(line?.vat ?? line?.vatPersentage ?? 0);
        let amount = Number(line?.grossAmount ?? line?.amount ?? 0);
        let discountAmount = Number(line?.discountAmount ?? line?.discountVal ?? 0);
        let amountAfterDiscount = Number(line?.netAmount ?? line?.amountAfterDiscount ?? 0);
        let vatAmount = Number(line?.vatAmount ?? 0);
        let amountAfterVat = Number(line?.lineAmount ?? line?.amountAfterVat ?? 0);

        // If rate/qty exist but totals were never recomputed (e.g. quotation punch), recalculate.
        if ((amount === 0 || amountAfterDiscount === 0 || amountAfterVat === 0) && quantity && rate) {
            const recalculated = calculateRowAmounts({ quantity, rate, discount, vat });
            amount = recalculated.amount;
            discountAmount = recalculated.discountAmount;
            amountAfterDiscount = recalculated.amountAfterDiscount;
            vatAmount = recalculated.vatAmount;
            amountAfterVat = recalculated.amountAfterVat;
        } else if (!amount && quantity && rate) {
            amount = quantity * rate;
            discountAmount = Number(line?.discountAmount ?? line?.discountVal ?? ((amount * discount) / 100));
            amountAfterDiscount = Number(line?.netAmount ?? line?.amountAfterDiscount ?? (amount - discountAmount));
            vatAmount = Number(line?.vatAmount ?? (amountAfterDiscount * vat) / 100);
            amountAfterVat = Number(line?.lineAmount ?? line?.amountAfterVat ?? (amountAfterDiscount + vatAmount));
        }

        return {
            orderNumber: line?.orderNumber || '',
            lineNumber: String(line?.lineNo ?? line?.lineNumber ?? ''),
            itemName: line?.itemName || '',
            itemNumber: line?.itemNo ?? line?.itemNumber ?? '',
            quantity,
            ...mapShipQtyFields({ ...line, qty: quantity }),
            rate,
            amount,
            discountAmount,
            discount,
            amountAfterDiscount,
            vat,
            vatAmount,
            amountAfterVat,
            pcs: line?.pcs,
            size: line?.size,
            itemGroup: line?.itemGroup,
            netWeight: line?.netWeight,
            grossWeight: line?.grossWeight,
            vatCode: line?.vatCode,
            status: line?.status ?? 20,
            m3LineStatus: line?.m3LineStatus ?? line?.m3lineStatus ?? line?.m3_line_status,
            orderLineId: line?.orderLineId ?? line?.orderId,
            referenceOrderNumber: line?.referenceOrderNumber ?? this.item?.referenceOrderId,
            baselineRate: round2(Number(line?.rate ?? 0)),
            baselineDiscount: clampDiscountPercent(line?.discount),
            priceUpdateStatus: 'idle',
            minrate: line?.minrate,
            avlStock: line?.avlStock,
            apiStock: line?.apiStock,
            stockError: line?.stockError,
            copyOrderStockError: line?.copyOrderStockError,
            cost: line?.cost ?? line?.costPrice,
            lineCost: line?.lineCost,
            minimumMargin: line?.minimumMargin ?? line?.itemMcm,
            calculatedMargin: line?.calculatedMargin,
            marginBelowMinimum: line?.marginBelowMinimum === true,
            noDecPl: resolveLineNoDecPl(line, 2)
        };
    }

    override fetchOrderLines(): void {
        this.v2ItemOrderHandler.fetchOrderLines({
            item: this.item,
            company: this.company,
            division: this.division,
            loadingSetter: (val: boolean) => (this.loading = val),
            loaderMessageSetter: (l1: string, l2: string) => {
                this.loaderMessageLine1 = l1;
                this.loaderMessageLine2 = l2;
            },
            itemListSetter: (list: any[]) => {
                this.itemList = list;
                seedPriceBaselines(this.itemList);
                this.refreshMarginFlagsOnItemList();
                this.loadOrderRecommendations({ force: true });
                if (!this.isViewOnlyOrderLines) {
                    queueMicrotask(() => this.applyWorkingPanelLayoutOnce(true));
                }
            },
            messageHandler: (msg) => this.v2MessageService.add(msg)
        });
    }

    override onItemListChange(updatedList: any[]): void {
        super.onItemListChange(updatedList);
        this.refreshMarginFlagsOnItemList();
        this.applyWorkingPanelLayoutOnce(true);
    }

    override onBrowseSelect(selectedRow: any): void {
        this.clearLineInfoSelection();
        super.onBrowseSelect(selectedRow);
        this.applyWorkingPanelLayoutOnce();
        if (selectedRow) {
            this.focusV2QuantityAfterDialogClose();
        }
    }

    /** Browse popup closes asynchronously; retry focus until the V2 qty input is reachable. */
    private focusV2QuantityAfterDialogClose(): void {
        if (this.readOnly || this.isReadOnly) {
            return;
        }
        const tryFocus = () => this.v2AddRow?.focusQuantity();
        [0, 100, 300, 600].forEach((delay) => setTimeout(tryFocus, delay));
    }

    onV2DeleteRow(row: V2OrderLine): void {
        const index = (this.itemList || []).findIndex((r) => `${r?.lineNumber}` === `${row?.lineNumber}` && `${r?.itemNumber}` === `${row?.itemNumber}`);
        if (index >= 0) {
            this.deleteItem(index);
            // Reuse last reco fetch — put deleted item back in the panel without RECO_LOAD + 10× stock.
            this.refreshRecommendationsFromCacheAfterGridChange();
        }
    }

    /**
     * After delete (or similar grid-only change): re-filter lastFetchedRecommendations against
     * the current grid and fetch stock only for newly visible cards. No GetOrderRecommendations.
     */
    private refreshRecommendationsFromCacheAfterGridChange(): void {
        if (!this.showRecommendationsPanel) {
            return;
        }
        if (!this.lastFetchedRecommendations.length || this.recommendationsAvailability === 'UNKNOWN') {
            this.loadOrderRecommendations({ force: true });
            return;
        }
        apiTraceFlow('RECO_SKIP', { reason: 'delete_local' });
        const exclude = this.gridItemNumbersForRecommendations();
        this.applyRecommendationOverlapToGridLines(this.lastFetchedRecommendations);
        this.orderRecommendations = this.filterRecommendationsLocally(
            this.lastFetchedRecommendations,
            exclude
        );
        this.enrichVisibleRecommendationStock(
            this.orderRecommendations.slice(0, this.recommendationVisibleLimit),
            this.recommendationsLoadSeq,
            'fillMissing'
        );
    }

    override submitItem(): void {
        const beforeCount = (this.itemList || []).length;
        const addedItemId = String(this.itemForm?.itemNumber ?? '').trim() || null;
        this.applyRecommendationAuditBeforeAdd();
        this.pendingFocusItemSearch = true;
        super.submitItem();
        setTimeout(() => {
            const afterCount = (this.itemList || []).length;
            if (afterCount > beforeCount) {
                this.logger.debug('V2 add-row: item added, focus moved to item input');
                this.pendingRecommendationSelect = null;
                this.refreshMarginFlagsOnItemList();
                this.applyWorkingPanelLayoutOnce(true);
                this.applyLocalRecommendationRemovalForAddedItem(addedItemId);
                return;
            }
            this.pendingFocusItemSearch = false;
            const validationMessage = this.getV2ValidationMessage();
            if (validationMessage) {
                this.v2MessageService.add({
                    severity: 'warn',
                    summary: 'Validation',
                    detail: validationMessage,
                    life: 3500
                });
            }
        });
    }

    /**
     * Tags the add-row form with recommendation audit before the base add path copies itemForm into itemList.
     * Select path uses pendingRecommendationSelect (item already removed from the panel).
     * Manual path looks up the current recommendations list.
     */
    private applyRecommendationAuditBeforeAdd(): void {
        const itemId = String(this.itemForm?.itemNumber ?? '').trim();
        if (!itemId) {
            return;
        }

        const pending = this.pendingRecommendationSelect;
        if (pending && pending.itemId === itemId) {
            this.itemForm.recommendationAddSource = 'RECOMMENDATION_SELECT';
            this.itemForm.recommendationWasRecommended = RECO_WAS_TRUE;
            this.itemForm.recommendationRank =
                pending.rank != null && Number.isFinite(Number(pending.rank)) ? Number(pending.rank) : null;
            this.itemForm.recommendationRunId = pending.runId;
            return;
        }

        this.itemForm.recommendationAddSource = 'MANUAL';
        if (this.recommendationsAvailability === 'NONE') {
            this.itemForm.recommendationWasRecommended = RECO_WAS_NO_RECO;
            this.itemForm.recommendationRank = null;
            this.itemForm.recommendationRunId = null;
            return;
        }
        if (this.recommendationsAvailability !== 'HAS') {
            // Still loading / panel off — leave null (do not invent false)
            this.itemForm.recommendationWasRecommended = null;
            this.itemForm.recommendationRank = null;
            this.itemForm.recommendationRunId = null;
            return;
        }

        const match = (this.lastFetchedRecommendations || []).find(
            (r) => String(r?.itemId ?? '').trim() === itemId
        );
        if (match) {
            this.itemForm.recommendationWasRecommended = RECO_WAS_TRUE;
            this.itemForm.recommendationRank =
                match.rank != null && Number.isFinite(Number(match.rank)) ? Number(match.rank) : null;
            this.itemForm.recommendationRunId = this.recommendationsRunId;
        } else {
            this.itemForm.recommendationWasRecommended = RECO_WAS_FALSE;
            this.itemForm.recommendationRank = null;
            this.itemForm.recommendationRunId = null;
        }
    }

    // Stock save gate lives in base submitOrder (incl. Save as Draft / immediate divisions).
    // Do not early-return on isImmediateDivision — that previously allowed Excel/copy oversell.

    onV2RestrictDecimals(payload: { event: any; field: 'quantity' | 'rate' | 'discount' }): void {
        this.restrictDecimals(payload.event, payload.field);
    }

    /**
     * V2 item input: do not wipe the typed value when replacing a prior dropdown selection,
     * and always drive search from the live input value (not only PrimeNG completeMethod).
     */
    onV2ItemInputChange(event: Event): void {
        if ((this as any).suppressItemInputReset) {
            return;
        }
        const input = event.target as HTMLInputElement | undefined;
        const query = (input?.value ?? this.itemForm?.FINALRESPNSE ?? '').trim();

        this.errorMessages.itemno = '';
        this.errorMessages.itemName = '';
        this.errorMessages.quantity = '';
        this.errorMessages.rate = '';
        this.errorMessages.discount = '';

        if (this.itemSelected) {
            this.itemForm.itemNumber = '';
            this.itemForm.itemName = '';
            this.itemForm.quantity = 0;
            this.itemForm.rate = 0;
            this.itemForm.amount = 0;
            this.itemForm.discount = 0;
            this.itemForm.amountAfterDiscount = 0;
            this.itemForm.vat = 0;
            this.itemForm.amountAfterVat = 0;
            this.itemForm.vatAmount = 0;
            this.itemSelected = false;
            this.resetAdditionalFields();
        }

        this.runV2ItemSearch(query);
    }

    private runV2ItemSearch(query: string): void {
        this.itemSearchQuery$.next(query);
    }

    override resetForm(): void {
        super.resetForm();
        this.resetV2ItemSearchUiState();
        if (this.pendingFocusItemSearch) {
            this.pendingFocusItemSearch = false;
            queueMicrotask(() => this.v2AddRow?.focusItemSearch());
        }
    }

    override clearItemForm(): void {
        super.clearItemForm();
        this.resetV2ItemSearchUiState();
    }

    private resetV2ItemSearchUiState(): void {
        this.itemSelected = false;
        this.filteredItemNames = [];
        this.v2AddRow?.clearSearchLoading();
    }

    override onItemNameSelect(selectedItem: any): void {
        this.clearLineInfoSelection();
        super.onItemNameSelect(selectedItem);
        this.applyWorkingPanelLayoutOnce();
    }

    /** Same Enter chain as V1 add form: Qty → Rate → Disc % → Add */
    onV2QuantityEnter(): void {
        this.errorMessages.quantity = '';
        this.onQuantityEnter();
        const qty = Number(this.itemForm?.quantity);
        const qtyOk = this.isCreditNoteOrder ? Math.abs(qty) >= 0.01 : qty > 0;
        if (qtyOk && !this.errorMessages.quantity) {
            this.v2AddRow?.focusRate();
        }
    }

    /** Recalculate amounts on rate blur without advancing focus. */
    onV2RateBlur(): void {
        if (!this.isManagerOrSuperUser && this.itemForm.rate < this.minRate) {
            return;
        }
        this.errorMessages.rate = '';
        Object.assign(this.itemForm, calculateRowAmounts(this.itemForm));
    }

    override onRateEnter(): void {
        super.onRateEnter();
        if (this.errorMessages.rate) {
            return;
        }
        if (this.division !== '400') {
            this.v2AddRow?.focusDiscount();
        }
    }

    override focusSubmitBtn(): void {
        if (this.v2AddRow) {
            setTimeout(() => this.v2AddRow?.focusAddButton());
            return;
        }
        super.focusSubmitBtn();
    }

    override getTotalWeight(): number {
        return (this.itemList || []).reduce((sum: number, line: any) => {
            const qty = effectiveShippedQty(line);
            const grossWeight = Number(line?.grossWeight || line?.weight || 0);
            return sum + qty * grossWeight;
        }, 0);
    }

    private getV2ValidationMessage(): string | null {
        const errors = this.errorMessages || {};
        const entries: Array<[string, string]> = [
            ['itemName', (errors as any).itemName],
            ['itemno', (errors as any).itemno],
            ['quantity', (errors as any).quantity],
            ['rate', (errors as any).rate],
            ['discount', (errors as any).discount]
        ];

        for (const [field, message] of entries) {
            const detail = (message || '').trim();
            if (!detail) {
                continue;
            }
            if (field === 'quantity' && (detail.includes('Out of Stock') || detail.includes('Max available stock'))) {
                // Base flow already emits dedicated stock toasts.
                continue;
            }
            return detail;
        }
        return null;
    }

    private setupV2ItemSearchPipelines(): void {
        if (this.v2SearchPipelineSubscribed) {
            return;
        }
        this.v2SearchPipelineSubscribed = true;
        this.itemSearchQuery$
            .pipe(
                map((q) => q.trim()),
                distinctUntilChanged(),
                debounce((q) => (q.length < 3 ? timer(0) : timer(400))),
                switchMap((query) => {
                    if (query.length < 3) {
                        this.filteredItemNames = [];
                        this.v2AddRow?.clearSearchLoading();
                        return EMPTY;
                    }
                    const seq = ++this.v2ItemSearchSeq;
                    this.v2AddRow?.setSearchLoading(true);
                    return this.v2ItemDetailsService
                        .searchItemsV2(query + '*', this.company, this.division, this.warehouse)
                        .pipe(
                            map((resp) => ({ seq, resp })),
                            catchError(() => of({ seq, resp: [] as any[] })),
                            finalize(() => this.v2AddRow?.clearSearchLoading())
                        );
                }),
                takeUntil(this.v2SearchDestroy$)
            )
            .subscribe(({ seq, resp }) => {
                if (seq !== this.v2ItemSearchSeq) {
                    return;
                }
                this.filteredItemNames = this.formatV2SearchDisplay(this.applyItemGroupExclusion(resp));
                if (this.filteredItemNames.length > 0) {
                    queueMicrotask(() => this.v2AddRow?.showItemSearchPanel());
                }
            });
    }

    /** Dropdown/input label: item number + finalized description (V2 search only). */
    private formatV2SearchDisplay(resp: any[]): any[] {
        if (!Array.isArray(resp) || resp.length === 0) {
            return [];
        }
        return resp.map((item) => {
            const itno = String(item?.MBITNO ?? '').trim();
            const label = String(item?.FINALRESPNSE ?? '').trim();
            if (!itno || !label) {
                return item;
            }
            const separator = ' | ';
            const combined = `${itno}${separator}${label}`;
            if (label.startsWith(itno + separator) || label.startsWith(itno + ' ')) {
                return item;
            }
            return { ...item, FINALRESPNSE: combined };
        });
    }

    private applyItemGroupExclusion(resp: any[]): any[] {
        const excludedGroups = ['199001', '114003', '114002'];
        if (!Array.isArray(resp) || resp.length === 0) {
            return [];
        }
        const hasMMITGR = resp.some((item) => 'MMITGR' in item && item.MMITGR !== undefined && item.MMITGR !== null);
        return hasMMITGR ? resp.filter((item) => !excludedGroups.includes(item.MMITGR)) : resp;
    }

    get canUploadExcel(): boolean {
        return !this.readOnly && !this.isReadOnly && this.hasValidCustomerId && !this.excelImportInProgress;
    }

    get canDownloadOrderLines(): boolean {
        return !this.loading && Array.isArray(this.itemList) && this.itemList.length > 0;
    }

    downloadExcelTemplate(): void {
        this.excelImportService.downloadTemplate();
    }

    downloadOrderLines(): void {
        if (!this.canDownloadOrderLines) {
            this.v2MessageService.add({
                severity: 'warn',
                summary: 'Download Lines',
                detail: 'No order lines available to download.',
                life: 3500
            });
            return;
        }

        const warehouse = String(
            this.item?.warehouseId || this.item?.warehouse || this.warehouse || ''
        );

        try {
            this.excelImportService.downloadOrderLines(this.itemList, {
                orderNumber: String(this.item?.orderNumber || 'order'),
                includeDiscount: this.division !== '400',
                includeMarginColumns: showLineMarginColumns(
                    this.division,
                    this.isCreditNoteOrder,
                    this.v2Storage,
                    warehouse
                )
            });
        } catch (err) {
            console.error('Order lines download failed', err);
            this.v2MessageService.add({
                severity: 'error',
                summary: 'Download Lines',
                detail: 'Could not download order lines.',
                life: 4000
            });
        }
    }

    openExcelImportDialog(): void {
        if (!this.canUploadExcel) {
            return;
        }
        if (this.isCreditNoteOrder) {
            this.v2MessageService.add({
                severity: 'warn',
                summary: 'Excel upload',
                detail: 'Excel upload is not available for credit note orders.',
                life: 3500
            });
            return;
        }
        this.showExcelImportDialog = true;
    }

    onExcelImportDownload(): void {
        this.downloadExcelTemplate();
        this.showExcelImportDialog = false;
    }

    onExcelImportUpload(): void {
        this.showExcelImportDialog = false;
        this.excelFileInput?.nativeElement?.click();
    }

    onExcelFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file || !this.canUploadExcel) {
            return;
        }

        const cuno = this.item?.customerId || this.item?.customer || '';
        const whlo = this.item?.warehouseId || this.item?.whsId || this.item?.warehouse || this.item?.whlo || this.warehouse || '';
        if (!cuno || !whlo) {
            this.v2MessageService.add({
                severity: 'error',
                summary: 'Excel upload',
                detail: 'Customer and warehouse are required before uploading articles.',
                life: 4000
            });
            return;
        }

        const startLineNumber =
            (this.itemList || []).length > 0
                ? Math.max(...(this.itemList || []).map((item) => Number(item.lineNumber) || 0)) + 1
                : 1;
        const orderId = this.v2Route.snapshot.paramMap.get('id');
        const currentOrderNumber = `${this.item?.orderNumber || ''}`.trim();
        const draftOrderKey = currentOrderNumber && currentOrderNumber !== 'New Order' ? currentOrderNumber : `${orderId || ''}`;

        this.excelImportInProgress = true;
        this.loading = true;
        this.loaderMessageLine1 = 'Importing articles from Excel';
        this.loaderMessageLine2 = 'Please wait...';
        apiTraceFlow('EXCEL_IMPORT', {
            company: this.company,
            division: this.division,
            customerId: cuno,
            warehouse: whlo
        });

        this.excelImportService
            .importRows(file, {
                company: this.company,
                division: this.division,
                cuno,
                whlo,
                defaultPriceList: this.product?.defaultPriceList || this.item?.defaultPriceList || '',
                shouldFetchVatPercentage: !!this.product?.shouldFetchVatPercentage,
                ruleActiveForDivision: this.ruleActiveForDivision,
                isCreditNoteOrder: this.isCreditNoteOrder,
                existingItemList: this.itemList || [],
                startLineNumber,
                draftOrderKey
            })
            .pipe(
                finalize(() => {
                    this.excelImportInProgress = false;
                    this.loading = false;
                })
            )
            .subscribe({
                next: (result) => {
                    if (!result.imported.length && result.skipped.length) {
                        this.excelSkippedRows = result.skipped;
                        this.showExcelSkippedDialog = true;
                        this.v2MessageService.add({
                            severity: 'error',
                            summary: 'Excel upload',
                            detail: 'No articles were imported. Review skipped rows for details.',
                            life: 4500
                        });
                        return;
                    }

                    if (result.imported.length) {
                        this.itemList = [...(this.itemList || []), ...result.imported];
                        localStorage.setItem('orderLines', JSON.stringify(this.itemList));
                        this.setItemNamesForAutocomplete();
                        this.refreshMarginFlagsOnItemListWithEnrichment();
                        this.applyWorkingPanelLayoutOnce(true);
                        // Force: EXCEL overlap needs a fresh fetch against imported grid items.
                        this.loadOrderRecommendations({ force: true });
                    }

                    this.excelSkippedRows = result.skipped;
                    if (result.skipped.length) {
                        this.showExcelSkippedDialog = true;
                    }

                    if (result.stockWarnings > 0) {
                        this.v2MessageService.add({
                            severity: 'warn',
                            summary: 'Excel upload',
                            detail: `${result.imported.length} article(s) imported. ${result.stockWarnings} line(s) have stock errors — fix before saving.`,
                            life: 5000
                        });
                    } else if (result.imported.length) {
                        this.v2MessageService.add({
                            severity: 'success',
                            summary: 'Excel upload',
                            detail:
                                result.skipped.length > 0
                                    ? `${result.imported.length} article(s) imported. ${result.skipped.length} row(s) skipped.`
                                    : `${result.imported.length} article(s) imported successfully.`,
                            life: 4000
                        });
                    }
                },
                error: (err: Error) => {
                    this.v2MessageService.add({
                        severity: 'error',
                        summary: 'Excel upload',
                        detail: err?.message || 'Could not read or parse the Excel file.',
                        life: 4500
                    });
                }
            });
    }

    canRefreshM3LineStatuses(): boolean {
        const orderNumber = `${this.item?.orderNumber || ''}`.trim();
        if (!orderNumber || orderNumber === 'New Order') {
            return false;
        }
        const ref = `${this.item?.referenceOrderId || this.product?.referenceOrderId || ''}`.trim();
        return !!ref;
    }

    hasM3ReferenceOrder(): boolean {
        return !!`${this.item?.referenceOrderId || this.product?.referenceOrderId || ''}`.trim();
    }

    /** Header gate: both m3LowestStatus and m3HighestStatus from GetOrderHeader are &lt; 66. */
    get orderPriceEditable(): boolean {
        return canEditPricesFromOrderHeader(this.item || this.product);
    }

    private priceEditOpts() {
        return {
            hasReferenceOrder: true as const,
            roleReadOnly: this.isReadOnly,
            orderPriceEditable: this.orderPriceEditable
        };
    }

    /** Synced order whose header M3 status range is entirely before 66. */
    get showEditPricesButton(): boolean {
        if (!environment.enableM3PriceUpdate) {
            return false;
        }
        if (this.isReadOnly || !this.readOnly || !this.hasM3ReferenceOrder()) {
            return false;
        }
        return this.orderPriceEditable;
    }

    hasEligiblePriceLines(): boolean {
        if (!this.orderPriceEditable) {
            return false;
        }
        return (this.itemList || []).some((row) => canEditPriceInM3(row, this.priceEditOpts()));
    }

    /**
     * Update Prices: visible while price-edit mode is on, or when there are unsaved dirty prices
     * (so exiting Done does not hide the commit action).
     */
    get showUpdatePricesButton(): boolean {
        if (!environment.enableM3PriceUpdate) {
            return false;
        }
        if (this.isReadOnly || !this.readOnly || !this.hasM3ReferenceOrder()) {
            return false;
        }
        return this.priceEditMode || this.hasDirtyEligiblePriceLines();
    }

    hasDirtyEligiblePriceLines(): boolean {
        return (this.itemList || []).some(
            (row) => canEditPriceInM3(row, this.priceEditOpts()) && isPriceDirtyVsBaseline(row)
        );
    }

    /** Enabled when dirty eligible lines exist and no request in flight. */
    canUpdatePricesInM3(): boolean {
        return this.showUpdatePricesButton && !this.updatePricesInFlight && this.hasDirtyEligiblePriceLines();
    }

    getEditPricesTooltip(): string {
        if (!this.hasM3ReferenceOrder()) {
            return 'Sync this order to M3 before editing prices';
        }
        if (!this.orderPriceEditable) {
            const lowest = `${this.item?.m3LowestStatus ?? this.product?.m3LowestStatus ?? ''}`.trim() || '—';
            const highest = `${this.item?.m3HighestStatus ?? this.product?.m3HighestStatus ?? ''}`.trim() || '—';
            return `Price edit requires M3 lowest and highest status both below 66 (now ${lowest}–${highest}).`;
        }
        return this.priceEditMode
            ? 'Exit price edit (Qty stays locked; only Rate and Disc % were editable)'
            : 'Edit Rate and Disc % (order M3 status is before 66)';
    }

    getUpdatePricesTooltip(): string {
        if (!this.hasM3ReferenceOrder()) {
            return 'Sync this order to M3 before updating prices';
        }
        if (this.updatePricesInFlight) {
            return 'Price update in progress…';
        }
        if (!this.priceEditMode && !this.hasDirtyEligiblePriceLines()) {
            return 'Click Edit Prices, change Rate/Disc %, then Update Prices';
        }
        if (!this.hasDirtyEligiblePriceLines()) {
            return 'Change rate or discount, then click Update Prices';
        }
        return 'Push changed prices to M3 (UpdPriceInfo) and save in Torna';
    }

    togglePriceEditMode(): void {
        if (!environment.enableM3PriceUpdate) {
            return;
        }
        if (this.isReadOnly || !this.readOnly || !this.hasM3ReferenceOrder() || !this.orderPriceEditable) {
            return;
        }
        if (!this.priceEditMode) {
            // Leave full bulk-edit if active — price mode must not unlock Qty.
            if (this.linesBulkEdit && this.linesGrid) {
                this.linesGrid.exitBulkEditQuietly();
                this.linesBulkEdit = false;
            }
            this.priceEditMode = true;
            return;
        }
        this.priceEditMode = false;
    }

    /** Full-line Edit (Qty/Rate/Disc) — mutually exclusive with price-edit mode. */
    onToggleBulkEdit(): void {
        if (this.priceEditMode) {
            this.priceEditMode = false;
        }
        this.linesGrid?.toggleBulkEdit();
    }

    updatePricesInM3(): void {
        if (!environment.enableM3PriceUpdate) {
            return;
        }
        if (!this.canUpdatePricesInM3() || this.updatePricesInFlight) {
            return;
        }
        const m3OrderNumber = `${this.item?.referenceOrderId || this.product?.referenceOrderId || ''}`.trim();
        const dirtyLines = (this.itemList || []).filter(
            (row) => canEditPriceInM3(row, this.priceEditOpts()) && isPriceDirtyVsBaseline(row)
        );
        if (!dirtyLines.length) {
            this.v2MessageService.add({ severity: 'info', summary: 'Update Prices', detail: 'No price changes', life: 2500 });
            return;
        }

        const allLinePayloads = dirtyLines.map((row) => ({
            orderLineId: row.orderLineId ?? null,
            lineNo: String(row.lineNumber ?? ''),
            rate: round2(Number(row.rate) || 0),
            discount: clampDiscountPercent(row.discount),
            previousRate: round2(Number(row.baselineRate ?? row.rate) || 0),
            previousDiscount: clampDiscountPercent(row.baselineDiscount ?? row.discount)
        }));

        // Chunk to avoid gateway timeouts / M3 hammering on large orders (100+ lines).
        const CHUNK_SIZE = 25;
        const chunks: typeof allLinePayloads[] = [];
        for (let i = 0; i < allLinePayloads.length; i += CHUNK_SIZE) {
            chunks.push(allLinePayloads.slice(i, i + CHUNK_SIZE));
        }

        const basePayload = {
            company: this.company,
            division: this.division,
            orderNumber: `${this.item?.orderNumber || ''}`.trim(),
            m3OrderNumber
        };

        this.updatePricesInFlight = true;
        dirtyLines.forEach((row) => {
            row.priceUpdateStatus = 'updating';
            row.priceUpdateMessage = '';
        });
        this.loading = true;
        this.loaderMessageLine1 = 'Updating prices in M3...';
        this.loaderMessageLine2 =
            chunks.length > 1 ? `Batch 1 of ${chunks.length} (${dirtyLines.length} lines)...` : 'Please wait...';

        let stopFurtherChunks = false;

        from(chunks)
            .pipe(
                concatMap((lines, index) => {
                    if (stopFurtherChunks) {
                        return of({
                            results: lines.map((l) => ({
                                lineNo: l.lineNo,
                                changed: true,
                                success: false,
                                message: 'Skipped because the order is locked in M3. Retry after the lock is released.'
                            })),
                            updated: 0,
                            failed: lines.length,
                            skipped: 0,
                            orderLocked: true,
                            summaryMessage: ''
                        });
                    }
                    this.loaderMessageLine2 =
                        chunks.length > 1
                            ? `Batch ${index + 1} of ${chunks.length} (${dirtyLines.length} lines)...`
                            : 'Please wait...';
                    return this.v2ItemDetailsService.updatePricesInM3({ ...basePayload, lines }).pipe(
                        catchError((err) =>
                            of({
                                results: lines.map((l) => ({
                                    lineNo: l.lineNo,
                                    changed: true,
                                    success: false,
                                    message:
                                        err?.error?.message ||
                                        err?.error?.summaryMessage ||
                                        err?.message ||
                                        'Update Prices request failed'
                                })),
                                updated: 0,
                                failed: lines.length,
                                skipped: 0,
                                orderLocked: false,
                                summaryMessage:
                                    err?.error?.message ||
                                    err?.error?.summaryMessage ||
                                    err?.message ||
                                    'Update Prices request failed'
                            })
                        ),
                        map((res: any) => {
                            if (res?.orderLocked) {
                                stopFurtherChunks = true;
                            }
                            return res;
                        })
                    );
                }),
                reduce(
                    (acc: any, res: any) => ({
                        results: [...(acc.results || []), ...(Array.isArray(res?.results) ? res.results : [])],
                        updated: (acc.updated || 0) + (Number(res?.updated) || 0),
                        failed: (acc.failed || 0) + (Number(res?.failed) || 0),
                        skipped: (acc.skipped || 0) + (Number(res?.skipped) || 0),
                        orderLocked: !!(acc.orderLocked || res?.orderLocked),
                        summaryMessage: acc.summaryMessage || res?.summaryMessage || ''
                    }),
                    { results: [], updated: 0, failed: 0, skipped: 0, orderLocked: false, summaryMessage: '' }
                ),
                finalize(() => {
                    this.updatePricesInFlight = false;
                    this.loading = false;
                })
            )
            .subscribe({
                next: (res: any) => {
                    this.applyUpdatePricesResults(dirtyLines, res);
                },
                error: (err) => {
                    // Should rarely hit — chunk errors are mapped above.
                    dirtyLines.forEach((row) => {
                        row.priceUpdateStatus = 'failed';
                        row.priceUpdateMessage = err?.error?.message || err?.message || 'Update Prices request failed';
                    });
                    this.v2MessageService.add({
                        severity: 'error',
                        summary: 'Update Prices',
                        detail: err?.error?.message || err?.message || 'Failed to update prices',
                        life: 8000
                    });
                    this.itemList = [...(this.itemList || [])];
                }
            });
    }

    private applyUpdatePricesResults(dirtyLines: any[], res: any): void {
        const results = Array.isArray(res?.results) ? res.results : [];
        const byLine = new Map<string, any>(results.map((r: any) => [String(r.lineNo ?? ''), r]));
        for (const row of dirtyLines) {
            const key = String(row.lineNumber ?? '');
            const r = byLine.get(key);
            if (!r) {
                row.priceUpdateStatus = 'failed';
                row.priceUpdateMessage = 'No result returned for this line';
                continue;
            }
            if (r.success) {
                if (r.changed === false) {
                    row.priceUpdateStatus = 'idle';
                    row.baselineRate = round2(Number(row.rate) || 0);
                    row.baselineDiscount = clampDiscountPercent(row.discount);
                    continue;
                }
                row.rate = Number(r.rate ?? row.rate);
                row.discount = Number(r.discount ?? row.discount);
                if (r.grossAmount != null) row.amount = Number(r.grossAmount);
                if (r.discountAmount != null) row.discountAmount = Number(r.discountAmount);
                if (r.netAmount != null) row.amountAfterDiscount = Number(r.netAmount);
                if (r.vatAmount != null) row.vatAmount = Number(r.vatAmount);
                if (r.lineAmount != null) row.amountAfterVat = Number(r.lineAmount);
                Object.assign(row, calculateRowAmounts(row));
                row.baselineRate = round2(Number(row.rate) || 0);
                row.baselineDiscount = clampDiscountPercent(row.discount);
                row.priceUpdateStatus = 'updated';
                row.priceUpdateMessage = r.message || 'Price updated';
            } else if (r.inconsistency) {
                row.priceUpdateStatus = 'inconsistency';
                row.priceUpdateMessage = r.message || 'Updated in M3 but Torna save failed';
            } else {
                row.priceUpdateStatus = 'failed';
                row.priceUpdateMessage = r.message || 'Update failed';
            }
        }

        const updated = Number(res?.updated) || 0;
        const failed = Number(res?.failed) || 0;
        const detail = this.buildUpdatePricesToastDetail(res);
        this.v2MessageService.add({
            severity: failed > 0 && updated === 0 ? 'error' : failed > 0 ? 'warn' : 'success',
            summary: 'Update Prices',
            detail,
            life: failed > 0 ? 9000 : 4500
        });
        this.itemList = [...(this.itemList || [])];
        this.refreshMarginFlagsOnItemList();
        this.onItemListChange(this.itemList);
        this.onSummaryChange(buildSummaryFromLines(this.itemList));

        // Same as Save Order: only leave when every change succeeded (no failures / lock).
        // Clears local draft so returning to Orders does not show "Draft Order Found".
        if (failed === 0 && updated > 0 && !res?.orderLocked) {
            this.priceEditMode = false;
            localStorage.removeItem('orders');
            localStorage.removeItem('routeParams');
            localStorage.removeItem('orderLines');
            setTimeout(() => this.goBack(), 500);
        }
    }

    /** Prefer backend summaryMessage (includes real M3 text e.g. order locked). */
    private buildUpdatePricesToastDetail(res: any): string {
        const summary = `${res?.summaryMessage || ''}`.trim();
        if (summary) {
            return summary.length > 320 ? summary.slice(0, 317) + '…' : summary;
        }
        const updated = Number(res?.updated) || 0;
        const failed = Number(res?.failed) || 0;
        const counts = `${updated} updated, ${failed} failed`;
        const firstError = (Array.isArray(res?.results) ? res.results : [])
            .map((r: any) => `${r?.message || ''}`.trim())
            .find((m: string) => !!m && !/^price updated$/i.test(m) && !/unchanged/i.test(m));
        if (firstError) {
            const combined = `${counts}. ${firstError}`;
            return combined.length > 320 ? combined.slice(0, 317) + '…' : combined;
        }
        return counts;
    }

    getRefreshM3LineStatusesTooltip(): string {
        if (this.canRefreshM3LineStatuses()) {
            return 'Get the latest line status from M3 for this order';
        }
        const orderNumber = `${this.item?.orderNumber || ''}`.trim();
        if (!orderNumber || orderNumber === 'New Order') {
            return 'Save the order first';
        }
        return 'Sync this order to M3 before refreshing line status';
    }

    private formatRefreshLineStatusSuccessMessage(updatedLines: number): string {
        const n = Number(updatedLines) || 0;
        if (n === 1) {
            return 'Line status updated for 1 line.';
        }
        return `Line status updated for ${n} lines.`;
    }

    refreshM3LineStatuses(): void {
        if (!this.canRefreshM3LineStatuses()) {
            this.v2MessageService.add({
                severity: 'warn',
                summary: 'Refresh line status',
                detail: this.getRefreshM3LineStatusesTooltip(),
                life: 4000
            });
            return;
        }

        const orderNumber = `${this.item.orderNumber}`.trim();
        this.loading = true;
        this.loaderMessageLine1 = 'Refreshing line status';
        this.loaderMessageLine2 = 'Please wait...';

        this.v2ItemDetailsService
            .refreshM3LineStatuses(orderNumber, this.company, this.division)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (res) => {
                    this.v2MessageService.add({
                        severity: 'success',
                        summary: 'Refresh line status',
                        detail: this.formatRefreshLineStatusSuccessMessage(res?.updatedLines ?? 0),
                        life: 4500
                    });
                    this.fetchOrderLines();
                },
                error: (err) => {
                    const detail =
                        err?.error?.message ||
                        err?.message ||
                        'Could not refresh line status. Please try again.';
                    this.v2MessageService.add({
                        severity: 'error',
                        summary: 'Refresh line status',
                        detail,
                        life: 5000
                    });
                }
            });
    }

    protected override resolveBulkLinesSuccessHandler(): ((orderNumber: string) => boolean | void) | undefined {
        return (orderNumber: string) => this.handleBulkLinesSuccessAfterDraft(orderNumber);
    }

    private handleBulkLinesSuccessAfterDraft(savedOrderNumber: string): boolean {
        if (!this.isImmediateDivision) {
            return false;
        }
        const orderNumber = (savedOrderNumber || this.item?.orderNumber || '').trim();
        if (!orderNumber) {
            return false;
        }
        if (this.item) {
            this.item.orderNumber = orderNumber;
        }
        if (!this.isOrderSyncReadyAfterDraftSave()) {
            return false;
        }
        this.syncAfterDraftOrderNumber = orderNumber;
        this.syncAfterDraftDialogVisible = true;
        return true;
    }

    private isOrderSyncReadyAfterDraftSave(): boolean {
        if (!this.isImmediateDivision) {
            return false;
        }
        const customerId = String(this.item?.customerId || this.product?.customerId || '').trim();
        if (!customerId) {
            return false;
        }
        if (this.shouldShowWh101OrderTypeWarning()) {
            return false;
        }
        if (this.isSaveBlockedByStock) {
            return false;
        }

        const minBusinessQty = 0.01;
        const minBusinessRate = 0.01;
        const lines = (this.itemList || []).filter((line: any) => line && line.itemNumber);
        if (!lines.length) {
            return false;
        }
        const hasInvalidLine = lines.some((line: any) => {
            const qty = Number(line.quantity ?? 0);
            const rate = Number(line.rate ?? 0);
            if (this.isCreditNoteOrder) {
                return Math.abs(qty) <= minBusinessQty || rate <= minBusinessRate;
            }
            return qty <= minBusinessQty || rate <= minBusinessRate;
        });
        if (hasInvalidLine) {
            return false;
        }

        if (
            this.isCreditLimitCheckEnabledForDivision &&
            !this.shouldSkipCreditLimitCheckForOrder() &&
            isCreditRestrictionActive(this.getCreditRestrictionState())
        ) {
            return false;
        }

        if (this.isMarginApprovalEnabled && !this.isCreditNoteOrder) {
            if (
                !shouldSkipMarginProcessingGate(
                    this.item?.referenceOrderId ?? this.product?.referenceOrderId,
                    this.item ?? this.product
                ) &&
                !isMarginApprovedOnOrder(this.item) &&
                !isMarginApprovedOnOrder(this.product)
            ) {
                (this.itemList || []).forEach((line) => Object.assign(line, calculateRowAmounts(line)));
                const summary = this.buildCurrentMarginApprovalSummary();
                if (summary?.hasAnyViolation) {
                    return false;
                }
            }
        }

        return true;
    }

    onSyncAfterDraftAccept(): void {
        const orderNumber = this.syncAfterDraftOrderNumber || this.item?.orderNumber || '';
        this.syncAfterDraftOrderNumber = '';
        this.syncAfterDraftDialogVisible = false;
        const customerName = this.item?.customerName || this.product?.customerName || this.item?.name || '';
        const item = {
            ...this.item,
            ...this.product,
            orderNumber,
            customerName
        };
        this.v2Router.navigate(['/preview-invoice'], {
            state: { item, isPreview: true, fromSync: true }
        });
    }

    onSyncAfterDraftDecline(): void {
        this.syncAfterDraftOrderNumber = '';
        this.syncAfterDraftDialogVisible = false;
        this.v2Router.navigate(['/Orders1']);
    }

    onSyncAfterDraftDialogHide(): void {
        if (!this.syncAfterDraftOrderNumber) {
            return;
        }
        this.syncAfterDraftOrderNumber = '';
        this.v2Router.navigate(['/Orders1']);
    }
}






